import { formatCurrency } from 'common/FormatUtilities';
import { first, isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React from 'react';
import CalendarCheck from 'src/assets/svg/shef/calendar-check.svg';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { DishFirstQuickAdd, EnticementChip, RatingChip } from 'src/shared/design-system/DishFirstCard';
import { ChipContainer } from 'src/shared/design-system/DishFirstCard/styles';
import { match, P } from 'ts-pattern';
import { Icon } from 'web-common/src/shared/design-system/components/Icon';
import { getCardAvailabilityFooterText, getTopKCuisines } from '../DishFirstCard/utils';
import {
  CuisineListing,
  Description,
  DishCard,
  FoodImage,
  FoodName,
  NotAvailableFooter,
  NotAvailableFooterText,
  Price,
  ShefName,
  StyledServingSizeChip,
} from './styles';
import { DishCardProps } from './types';

const IMAGE_WIDTH = 238;
const IMAGE_HEIGHT = 160;
const MIN_RATINGS_TO_SHOW = 1;

// NOTE: This should be renamed to DishCard if/when we remove the legacy explore page
export const DishFirstDishCard: React.FC<DishCardProps> = (props) => {
  const {
    foodItemPreview,
    segment,
    onClick,
    href,
    increment,
    decrement,
    isFluid = false,
    quantity = 0,
    minQuantity = 0,
    maxQuantity = 0,
    scrollPosition,
    showDescription = false,
    showShefInfo = true,
    className,
    quickAddLoading = false,
    quickAddError = false,
    shortCard = false,
    showDetails = true,
    isShefAvailable = true,
    nextDeliveryDate = null,
    isShefAvailableInFuture = true,
    dishBadge,
  } = props;
  const [deliveryDates] = usePersistentDeliveryDates();
  const selectedDeliveryDate = first(deliveryDates) ? DateTime.fromISO(first(deliveryDates) ?? '') : null;
  const isFlexible = isEmpty(selectedDeliveryDate);
  const showAvailabilityDate = isFlexible || !isShefAvailable;

  const { description, shef, cuisineCategories } = foodItemPreview;

  const foodNameRef = React.useRef<HTMLSpanElement>(null);

  const cuisines = getTopKCuisines({ cuisineCategories });

  // Just type-safety, this should not happen
  if (!foodItemPreview?.imageUrl || !foodItemPreview?.name) return null;

  const imageContent = (
    <>
      <FoodImage
        src={foodItemPreview?.imageUrl}
        alt={foodItemPreview.name}
        sourceWidth={IMAGE_WIDTH}
        sourceHeight={IMAGE_HEIGHT}
        scrollPosition={scrollPosition}
        effect={'opacity'}
      />
      <ChipContainer top left>
        <EnticementChip foodItem={foodItemPreview} segment={segment} />
      </ChipContainer>
      {dishBadge && (
        <ChipContainer top right>
          {dishBadge}
        </ChipContainer>
      )}
    </>
  );

  const servingSize = match(foodItemPreview)
    .with({ servingSize: P.not(P.nullish) }, ({ servingSize }) => servingSize)
    .with({ portion: P.not(P.nullish) }, ({ portion }) => portion)
    .otherwise(() => null);

  const availabilityContent = match({
    isShefAvailable,
    showAvailabilityDate,
    nextDeliveryDate,
    isShefAvailableInFuture,
    maxQuantity,
  })
    .with({ isShefAvailableInFuture: false }, () => (
      <NotAvailableFooter>
        <img src={CalendarCheck} alt='Calendar' />
        <NotAvailableFooterText>Not available this week</NotAvailableFooterText>
      </NotAvailableFooter>
    ))
    .with(
      {
        nextDeliveryDate: P.not(null),
        showAvailabilityDate: true,
      },
      () => (
        <NotAvailableFooter>
          <img src={CalendarCheck} alt='Calendar' />
          <NotAvailableFooterText>{getCardAvailabilityFooterText(nextDeliveryDate)}</NotAvailableFooterText>
        </NotAvailableFooter>
      )
    )
    .with({ maxQuantity: 0 }, () => (
      <NotAvailableFooter>
        <img src={CalendarCheck} alt='Calendar' />
        <NotAvailableFooterText>Sold out</NotAvailableFooterText>
      </NotAvailableFooter>
    ))
    .otherwise(() => null);

  const bodyContent = (
    <div>
      {showDetails && cuisines && <CuisineListing>{cuisines}</CuisineListing>}
      <FoodName ref={foodNameRef}>{foodItemPreview.name}</FoodName>
      <div style={{ display: 'flex' }}>
        {!!foodItemPreview?.averageRating && foodItemPreview?.ratingCount >= MIN_RATINGS_TO_SHOW && (
          <div style={{ marginRight: '6px' }}>
            <RatingChip averageRating={foodItemPreview.averageRating} count={foodItemPreview.ratingCount} showInline />
          </div>
        )}

        {servingSize && <StyledServingSizeChip text={servingSize} showInline showShadow={false} />}
      </div>
      <div className='mb-2'>{availabilityContent}</div>
      {showShefInfo && shef && <ShefName>{`Shef ${shef?.publicName}`}</ShefName>}
      {showDescription && description && <Description>{description}</Description>}
    </div>
  );
  const hasServingSizeCustomization = !isEmpty(foodItemPreview.servingSizeCustomizations);
  const { footerContent, useGrayBackground } = match({
    isShefAvailable,
    nextDeliveryDate,
    isShefAvailableInFuture,
    maxQuantity,
    hasServingSizeCustomization,
  })
    .with({ hasServingSizeCustomization: true }, () => ({
      footerContent: (
        <>
          <Price>Starting at {formatCurrency(foodItemPreview.price)}</Price>
          <Icon name='chevronRight' />
        </>
      ),
      useGrayBackground: false,
    }))
    .otherwise(() => ({
      footerContent: (
        <>
          <Price>{formatCurrency(foodItemPreview.price)}</Price>
          <DishFirstQuickAdd
            increment={increment}
            decrement={decrement}
            quantity={quantity}
            minQuantity={minQuantity}
            maxQuantity={maxQuantity}
            loading={quickAddLoading}
            error={quickAddError}
          />
        </>
      ),
      useGrayBackground: false,
    }));

  return (
    <DishCard
      isFluid={isFluid}
      onClick={onClick}
      href={href}
      className={className}
      imageContent={imageContent}
      bodyContent={bodyContent}
      footerContent={footerContent}
      shortCard={shortCard}
      useGrayBackground={useGrayBackground}
      data-cy='dish-card'
    />
  );
};
